import React from "react";
import { useNavigate } from "react-router-dom";
import { TestSet } from "../../model_gen/evaluation";
import { Button } from "../ui/button";
import { MoreHorizontalIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";

type UnifiedTestSet = TestSet & {
  type?: "REQUEST" | "WORKFLOW";
  resource_name?: string;
  resource_id?: number;
  item_count?: number;
};

interface TestSetsTableProps {
  testSets: UnifiedTestSet[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onDeleteTestSet: (testSet: UnifiedTestSet) => void;
  intentGroupId?: string;
  sortConfig?: {
    key: keyof TestSet | null;
    direction: "asc" | "desc";
  };
  onSort?: (key: keyof TestSet) => void;
}

const TestSetsTable: React.FC<TestSetsTableProps> = ({
  testSets,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onDeleteTestSet,
  intentGroupId,
  sortConfig,
  onSort,
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const getItemCount = (testSet: UnifiedTestSet): number =>
    testSet.item_count ?? (Number((testSet.meta as Record<string, unknown>)?.request_count) || 0);

  const columns: PaginatedTableColumn<UnifiedTestSet>[] = [
    {
      key: "name",
      header: "Name",
      width: "w-[18%]",
      sortable: !!onSort,
      render: (testSet) => (
        <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
          {testSet.name}
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      width: "w-[20%]",
      sortable: false,
      render: (testSet) => (
        <div className="text-sm font-gc-reg text-foreground truncate max-w-[200px]" title={testSet.description || ""}>
          {testSet.description || "No description provided"}
        </div>
      ),
    },
    {
      key: "type" as keyof UnifiedTestSet,
      header: "Type",
      width: "w-[10%]",
      sortable: false,
      render: (testSet) => (
        <span className={cn(
          "text-xs font-gc-med px-2 py-0.5 rounded",
          testSet.type === "WORKFLOW"
            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
            : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
        )}>
          {testSet.type === "WORKFLOW" ? "Workflow" : "Request"}
        </span>
      ),
    },
    {
      key: "resource_name" as keyof UnifiedTestSet,
      header: "Resource",
      width: "w-[15%]",
      sortable: false,
      render: (testSet) => {
        const resourceLabel = testSet.resource_name ?? (testSet as { intent_group?: { intent_name?: string } }).intent_group?.intent_name;
        return (
        <div>
          {resourceLabel ? (
            <button
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (testSet.type === "WORKFLOW" && testSet.application_id) {
                  navigate(`/application/${testSet.application_id}`);
                } else if (testSet.intent_group_id) {
                  navigate(`/intent-group/${testSet.intent_group_id}`);
                }
              }}
              className="text-sm font-gc-reg text-maitai-green hover:underline transition-colors"
            >
              {resourceLabel}
            </button>
          ) : (
            <div className="text-sm font-gc-reg text-muted-foreground">-</div>
          )}
        </div>
        );
      },
    },
    {
      key: "is_golden",
      header: "Golden",
      width: "w-[8%]",
      sortable: false,
      render: (testSet) => (
        <span className={cn(
          "text-sm font-gc-reg",
          testSet.is_golden ? "text-foreground" : "text-muted-foreground"
        )}>
          {testSet.type === "WORKFLOW" ? "-" : testSet.is_golden ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[14%]",
      sortable: !!onSort,
      render: (testSet) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(testSet.date_created ?? 0)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(testSet.date_created ?? 0)}
          </div>
        </div>
      ),
    },
    {
      key: "item_count" as keyof UnifiedTestSet,
      header: "Items",
      width: "w-[8%]",
      sortable: false,
      render: (testSet) => (
        <span className="text-sm font-gc-med text-foreground">
          {getItemCount(testSet)}
        </span>
      ),
    },
    {
      key: "id",
      header: "",
      width: "w-[48px]",
      sortable: false,
      render: (testSet: UnifiedTestSet) => (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              // Simple dropdown menu using native browser context menu
              const menu = document.createElement("div");
              menu.className = "absolute right-0 top-8 z-50 min-w-48 bg-popover border border-border rounded-md shadow-lg py-1";
              
              const deleteButton = document.createElement("button");
              deleteButton.className = "flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-red-600";
              deleteButton.innerHTML = `
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
                Delete
              `;
              deleteButton.onclick = (evt) => {
                evt.stopPropagation();
                if (window.confirm(`Are you sure you want to delete test set "${testSet.name}"?`)) {
                  onDeleteTestSet(testSet);
                }
                menu.remove();
              };
              menu.appendChild(deleteButton);
              
              // Position and show menu
              const buttonRect = e.currentTarget.getBoundingClientRect();
              const container = e.currentTarget.parentElement;
              if (container) {
                container.appendChild(menu);
                
                // Remove menu when clicking outside
                const handleClickOutside = (evt: Event) => {
                  if (!menu.contains(evt.target as Node)) {
                    menu.remove();
                    document.removeEventListener("click", handleClickOutside);
                  }
                };
                setTimeout(() => document.addEventListener("click", handleClickOutside), 0);
              }
            }}
          >
            <MoreHorizontalIcon className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  const handleRowClick = (testSet: UnifiedTestSet) => {
    if (testSet.type === "WORKFLOW") {
      navigate(`/test/workflow-test-sets/${testSet.id}`);
    } else if (testSet.intent_group_id) {
      navigate(`/intent-group/${testSet.intent_group_id}/testsets/${testSet.id}`);
    }
  };

  return (
    <PaginatedTable
      data={testSets}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle="No test sets found"
      emptyDescription="Create a new test set to evaluate your model's performance."
      onRowClick={handleRowClick}
      rowClassName="hover:bg-accent/20 transition-colors"
      showPagination={true}
    />
  );
};

export default TestSetsTable; 